'use client';

import type { FC } from 'react';
import { BottomSheet, EmptyState, Skeleton } from '../../components/ui';
import { SegmentedControl, type Segment } from '../../components/ui/SegmentedControl';
import { ErrorMessage } from '../../components/ErrorMessage';
import { UserAvatar } from '../../components/UserAvatar/UserAvatar';
import { FaCrown, RiArrowRightSLine } from '../../components/icons';
import { useDisplayUserName } from '../../hooks/useDisplayUserName';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { formatDateNumeric } from '../../i18n/formatDate';
import { cn } from '../../utils/cn';
import translations from './UpgradeScreen.translations';
import { UpgradePairLine } from './UpgradePairLine';
import { Credits } from '../../components/Credits';
import type { UpgradeTopEntry, UpgradeTopResponse } from '../../services/api';

export type UpgradeTopPeriod = 'day' | 'month';

interface UpgradeTopSheetProps {
  isOpen: boolean;
  onClose: () => void;
  period: UpgradeTopPeriod;
  onPeriodChange: (period: UpgradeTopPeriod) => void;
  data: UpgradeTopResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onUserClick: (userId: string) => void;
}

const formatChance = (probability: number) => {
  const percent = probability * 100;
  return `${percent < 10 ? percent.toFixed(1) : Math.round(percent)}%`;
};

const TopRow: FC<{ entry: UpgradeTopEntry; isMe: boolean; onClick: () => void }> = ({ entry, isMe, onClick }) => {
  const { t } = useLocalTranslation(translations);
  const displayUserName = useDisplayUserName();
  const rank = entry.rank;
  return (
    <button
      type="button"
      data-testid="upgrade-top-row"
      onClick={onClick}
      className={cn(
        'flex w-full flex-col gap-3 rounded-xl px-3 pt-2.5 pb-3 text-start transition active:opacity-80',
        isMe ? 'bg-accent-blue/15' : 'bg-fill-tertiary',
      )}
    >
      <span className="flex w-full items-center gap-2">
        <span
          className={cn(
            'w-6 shrink-0 text-center text-sm font-bold tabular-nums',
            rank === 1 ? 'text-price' : rank !== null && rank <= 3 ? 'text-label-secondary' : 'text-label-tertiary',
          )}
        >
          {rank === 1 ? <FaCrown className="mx-auto text-price" /> : (rank ?? '—')}
        </span>
        <UserAvatar userId={entry.userId} firstName={entry.firstName} lastName={entry.lastName} level={entry.level} size="s" />
        <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-label-primary">
          {isMe ? t('topYou') : displayUserName(entry.firstName, entry.lastName)}
        </span>
        <span dir="ltr" className="shrink-0 text-[17px] font-bold leading-5 tabular-nums text-accent-blue">×{entry.coefficient.toFixed(2)}</span>
        <RiArrowRightSLine aria-hidden size={18} className="-me-1 shrink-0 text-white/30 rtl:-scale-x-100" />
      </span>
      <UpgradePairLine
        source={entry.source}
        sourceAmount={entry.sourceAmount}
        target={entry.target}
        footer={
          <span className="mt-0.5 flex flex-wrap items-center gap-x-2.5 text-xs">
            <Credits amount={entry.target.price} iconSize={11} className="whitespace-nowrap font-semibold text-white/80" />
            <span className="whitespace-nowrap tabular-nums text-accent-green">{t('topChance', { chance: formatChance(entry.probability) })}</span>
          </span>
        }
      />
    </button>
  );
};

export const UpgradeTopSheet: FC<UpgradeTopSheetProps> = ({
  isOpen,
  onClose,
  period,
  onPeriodChange,
  data,
  isLoading,
  isError,
  onRetry,
  onUserClick,
}) => {
  const { t, lang } = useLocalTranslation(translations);

  const periods: Segment<UpgradeTopPeriod>[] = [
    { id: 'day', label: t('topPeriodDay') },
    { id: 'month', label: t('topPeriodMonth') },
  ];

  const resetNote = !data
    ? null
    : period === 'day'
      ? t('topResetDay')
      : t('topResetMonth', { date: formatDateNumeric(data.periodEnd, lang) });

  const me = data?.me ?? null;
  const meInList = !!me && !!data?.entries.some((e) => e.userId === me.userId);

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t('topTitle')}>
      <div className="flex flex-col gap-3 px-4 pb-4" data-testid="upgrade-top-sheet">
        <p className="m-0 text-[13px] leading-[18px] text-label-secondary">{t('topExplain')}</p>
        <SegmentedControl segments={periods} value={period} onChange={onPeriodChange} tone="solid" />
        {resetNote && <p className="m-0 -mt-1 text-center text-xs text-label-tertiary">{resetNote}</p>}

        {isLoading ? (
          <div className="flex flex-col gap-1.5">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-[68px] w-full rounded-xl" />
            ))}
          </div>
        ) : isError || !data ? (
          <ErrorMessage error={t('topError')} onRetry={onRetry} />
        ) : (
          <>
            {me && !meInList && <TopRow entry={me} isMe onClick={() => onUserClick(me.userId)} />}
            {data.entries.length === 0 ? (
              <EmptyState title={period === 'day' ? t('topEmptyDay') : t('topEmptyMonth')} />
            ) : (
              <div className="flex flex-col gap-1.5">
                {data.entries.map((entry) => (
                  <TopRow
                    key={entry.upgradeId}
                    entry={entry}
                    isMe={entry.userId === me?.userId}
                    onClick={() => onUserClick(entry.userId)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </BottomSheet>
  );
};
